import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

import { motion } from "framer-motion";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { 
  BanknotesIcon, 
  ChartBarIcon, 
  ShoppingBagIcon, 
  TruckIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon 
} from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import LoadingPage from "./Loader";
import RestaurantOrders from "./RestaurantOrders";
import DeliveryFeeManager from "./DeliveryFeeManager";
import RatingAnalytics from "./rating/RatingAnalytics";
import RatingsList from "./rating/RatingsList";
import { 
  showSuccessNotification, 
  showErrorNotification, 
  showInfoNotification, 
  showWarningNotification 
} from './NotificationManager';

// Register ChartJS components
ChartJS.register(
  ArcElement, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mongobyte-hc73.onrender.com/api/v1';

const RestaurantDashboard = () => {
  // Core Restaurant Data
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Orders Management
  const [orders, setOrders] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [confirmedOrders, setConfirmedOrders] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  
  // Revenue and Financial Data
  const [revenueData, setRevenueData] = useState({
    totalRevenue: 0,
    breakdown: {
      daily: [],
      weekly: [],
      monthly: [],
      fees: 0,
      foodAmount: 0
    }
  });
  const [isLoadingRevenue, setIsLoadingRevenue] = useState(false);
  
  // Transaction Data
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [transactionStats, setTransactionStats] = useState({
    totalEarnings: 0,
    totalWithdrawals: 0,
    totalOrders: 0,
    averageOrderValue: 0,
    currentBalance: 0,
    pendingWithdrawals: 0
  });
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  
  // Ratings and Reviews
  const [ratingsData, setRatingsData] = useState([]);
  const [ratingStats, setRatingStats] = useState({
    averageRating: 0,
    totalRatings: 0,
    ratingsBreakdown: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  });
  const [ratingAnalytics, setRatingAnalytics] = useState(null);
  const [isLoadingRatings, setIsLoadingRatings] = useState(false);
  
  // Menu Management
  const [menuItems, setMenuItems] = useState([]);
  const [isLoadingMenu, setIsLoadingMenu] = useState(false);

  // Memoize restaurant credentials to avoid re-decoding token
  const restaurantCredentials = useMemo(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      return null;
    }
    
    try {
      const decodedToken = jwtDecode(token);
      
      const customId = decodedToken.restaurant?.customId || decodedToken.customId;
      
      if (!customId) {
        return null;
      }
      
      return {
        token,
        customId
      };
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  }, []);

  // UI State Management
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showDeliveryFeeManager, setShowDeliveryFeeManager] = useState(false);
  
  // Date Range Management
  const [dateRange, setDateRange] = useState("week");
  const [dashboardStats, setDashboardStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    avgOrderValue: 0,
    totalCustomers: 0,
    pendingOrders: 0,
    completedOrders: 0,
    cancelledOrders: 0,
    orderGrowth: 0,
    revenueGrowth: 0,
    topMeals: [],
    recentOrders: [],
    monthlyData: {
      labels: [],
      revenue: [],
      orders: []
    },
    dailyData: {
      labels: [],
      revenue: [],
      orders: []
    }
  });
  
  // Date range options for reports and dashboard
  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'all', label: 'All Time' }
  ];

  // Update the fetchDashboardStats with date range support
  const calculateDateRange = useCallback(() => {
    const today = new Date();
    let startDate, endDate;
    
    switch(dateRange) {
      case 'today':
        startDate = format(today, 'yyyy-MM-dd');
        endDate = format(today, 'yyyy-MM-dd');
        break;
      case 'yesterday':
        const yesterday = subDays(today, 1);
        startDate = format(yesterday, 'yyyy-MM-dd');
        endDate = format(yesterday, 'yyyy-MM-dd');
        break;
      case 'week':
        const weekStart = startOfWeek(today, { weekStartsOn: 0 });
        const weekEnd = endOfWeek(today, { weekStartsOn: 0 });
        startDate = format(weekStart, 'yyyy-MM-dd');
        endDate = format(weekEnd, 'yyyy-MM-dd');
        break;
      case 'month':
        const monthStart = startOfMonth(today);
        const monthEnd = endOfMonth(today);
        startDate = format(monthStart, 'yyyy-MM-dd');
        endDate = format(monthEnd, 'yyyy-MM-dd');
        break;
      case 'all':
        startDate = '2020-01-01';
        endDate = format(today, 'yyyy-MM-dd');
        break;
      default:
        const defaultWeekStart = startOfWeek(today, { weekStartsOn: 0 });
        const defaultWeekEnd = endOfWeek(today, { weekStartsOn: 0 });
        startDate = format(defaultWeekStart, 'yyyy-MM-dd');
        endDate = format(defaultWeekEnd, 'yyyy-MM-dd');
    }
    
    return { startDate, endDate };
  }, [dateRange]);

  // Unified function to fetch all dashboard data from single endpoint
  const fetchDashboardData = useCallback(async () => {
    if (!restaurantCredentials) {
      setLoading(false);
      setIsLoadingRevenue(false);
      setIsLoadingOrders(false);
      setIsLoadingRatings(false);
      return;
    }

    if (!restaurantCredentials.customId) {
      setLoading(false);
      setIsLoadingRevenue(false);
      setIsLoadingOrders(false);
      setIsLoadingRatings(false);
      return;
    }

    setIsLoadingRevenue(true);
    setIsLoadingOrders(true);
    setIsLoadingRatings(true);
    
    try {
      // Use the new unified dashboard endpoint
      const response = await axios.get(
        `${API_BASE_URL}/restaurants/${restaurantCredentials.customId}/dashboard`,
        {
          headers: { 
            Authorization: `Bearer ${restaurantCredentials.token}` 
          },
          params: {
            dateRange
          }
        }
      );

      if (response.data.success) {
        const { stats, dailyData, recentOrders, ratingsBreakdown, period } = response.data.data;

        // Update all dashboard stats from unified response
        setDashboardStats(prev => ({
          ...prev,
          totalOrders: stats.totalOrders,
          totalRevenue: stats.totalRevenue,
          avgOrderValue: stats.avgOrderValue,
          pendingOrders: stats.pendingOrders,
          completedOrders: stats.deliveredOrders,
          cancelledOrders: stats.cancelledOrders,
          recentOrders: recentOrders,
          dailyData: {  
            labels: dailyData.map(item => item.label),
            revenue: dailyData.map(item => item.revenue),
            orders: dailyData.map(item => item.orderCount)
          }
        }));

        // Update rating stats
        setRatingStats({
          averageRating: stats.averageRating,
          totalRatings: stats.totalRatings,
          ratingsBreakdown: ratingsBreakdown
        });

        // Show success message for debugging
        
      } else {
        throw new Error('Dashboard API returned success: false');
      }
    } catch (error) {
      console.error("Error fetching unified dashboard data:", error);
      
      showErrorNotification("Failed to fetch dashboard statistics");
      
      // Fallback to individual endpoints if unified fails
      try {
        await Promise.allSettled([
          fetchOrders().catch(err => console.error("Fallback orders fetch failed:", err)),
          fetchRestaurantRevenue().catch(err => console.error("Fallback revenue fetch failed:", err)),
          fetchRatings().catch(err => console.error("Fallback ratings fetch failed:", err))
        ]);
      } catch (fallbackError) {
        console.error("Fallback fetches also failed:", fallbackError);
      }
    } finally {
      setIsLoadingRevenue(false);
      setIsLoadingOrders(false);
      setIsLoadingRatings(false);
    }
  }, [dateRange, restaurantCredentials]);

  // Fetch restaurant transaction history using the correct endpoint
  const fetchTransactionHistory = useCallback(async () => {
    if (!restaurantCredentials) {
      console.warn("No restaurant credentials available for transaction fetch");
      return;
    }

    setIsLoadingTransactions(true);
    try {
      const { startDate, endDate } = calculateDateRange();
      
      // Use the correct transaction endpoint for restaurants
      const response = await axios.get(
        `${API_BASE_URL}/transactions/restaurant/history`,
        {
          headers: { 
            Authorization: `Bearer ${restaurantCredentials.token}` 
          },
          params: {
            page: 1,
            limit: 50,
            startDate,
            endDate
          }
        }
      );

      if (response.data.success) {
        const { transactions, currentBalance } = response.data.data;
        setTransactionHistory(transactions);
        
        // Calculate transaction stats from the transaction data since there's no stats endpoint
        const totalEarnings = transactions
          .filter(t => t.direction === 'credit')
          .reduce((sum, t) => sum + (t.amount || 0), 0);
        
        const totalWithdrawals = transactions
          .filter(t => t.direction === 'debit' && t.type === 'restaurant_withdrawal')
          .reduce((sum, t) => sum + (t.amount || 0), 0);
        
        const totalOrders = transactions
          .filter(t => t.type === 'restaurant_earning')
          .length;
        
        const avgOrderValue = totalOrders > 0 ? totalEarnings / totalOrders : 0;
        
        setTransactionStats({
          totalEarnings,
          totalWithdrawals,
          totalOrders,
          averageOrderValue: avgOrderValue,
          currentBalance: currentBalance || 0,
          pendingWithdrawals: 0 // Will be updated by withdrawal data fetch
        });
        
        // Update restaurant balance from transaction API
        setRestaurant(prev => ({
          ...prev,
          walletBalance: currentBalance
        }));
      }
    } catch (error) {
      console.error("Error fetching transaction history:", error);
      showErrorNotification("Failed to fetch transaction history");
    } finally {
      setIsLoadingTransactions(false);
    }
  }, [calculateDateRange, restaurantCredentials]);

  // Fetch restaurant revenue using the correct endpoint
  const fetchRestaurantRevenue = useCallback(async () => {
    if (!restaurantCredentials) {
      console.warn("No restaurant credentials available for revenue fetch");
      return;
    }

    setIsLoadingRevenue(true);
    try {
      const { startDate, endDate } = calculateDateRange();
      
      // Use the correct restaurant revenue endpoint
      const response = await axios.get(
        `${API_BASE_URL}/restaurants/${restaurantCredentials.customId}/revenue`,
        {
          headers: { 
            Authorization: `Bearer ${restaurantCredentials.token}` 
          },
          params: {
            startDate,
            endDate
          }
        }
      );

      if (response.data) {
        const revenueData = response.data;
        setRevenueData(revenueData);
        
        // Update dashboard stats based on revenue data
        const totalRevenue = revenueData.totalRevenue || 0;
        const dailyData = revenueData.breakdown?.byDay || [];
        const monthlyData = revenueData.breakdown?.byMonth || [];
        
        // Calculate total orders from daily data
        const totalOrders = dailyData.reduce((total, day) => total + (day.orderCount || 0), 0);
        const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
        
        setDashboardStats(prev => ({
          ...prev,
          totalRevenue,
          totalOrders,
          avgOrderValue,
          monthlyData: {
            labels: monthlyData.map(item => item.month),
            revenue: monthlyData.map(item => item.revenue || 0),
            orders: monthlyData.map(item => item.orderCount || 0)
          },
          dailyData: {
            labels: dailyData.map(item => format(new Date(item.date), 'MMM dd')),
            revenue: dailyData.map(item => item.revenue || 0),
            orders: dailyData.map(item => item.orderCount || 0)
          }
        }));
      }
    } catch (error) {
      console.error("Error fetching restaurant revenue:", error);
      showErrorNotification("Failed to fetch revenue statistics");
    } finally {
      setIsLoadingRevenue(false);
    }
  }, [calculateDateRange, restaurantCredentials]);

  // Enhanced fetchOrders function that properly calculates stats for the selected date range
  const fetchOrders = useCallback(async (isRefreshRequest = false) => {
    if (!restaurantCredentials) {
      console.warn("No restaurant credentials available for orders fetch");
      return;
    }

    setIsLoadingOrders(true);
    try {
      // Use the correct orders endpoint for restaurants
      const response = await axios.get(
        `${API_BASE_URL}/orders/restaurant/${restaurantCredentials.customId}`,
        {
          headers: { 
            Authorization: `Bearer ${restaurantCredentials.token}` 
          }
        }
      );

      const ordersData = response.data || [];
      setOrders(ordersData);
      
      // Separate orders by status (handle new lowercase status values)
      const pending = ordersData.filter(order => order.status === 'Pending');
      const confirmed = ordersData.filter(order => order.status === 'Confirmed');
      
      setPendingOrders(pending);
      setConfirmedOrders(confirmed);
      
      // Filter orders based on current date range for dashboard stats
      const { startDate, endDate } = calculateDateRange();
      const filteredOrders = ordersData.filter(order => {
        // Use orderDate field (matches backend) instead of createdAt
        const orderDate = new Date(order.orderDate);
        const orderDateStr = format(orderDate, 'yyyy-MM-dd');
        return orderDateStr >= startDate && orderDateStr <= endDate;
      });
      
      // Calculate order statistics for the selected date range
      const totalOrdersInRange = filteredOrders.length;
      const totalRevenueFromOrders = filteredOrders.reduce((sum, order) => sum + (order.totalPrice || 0), 0);
      const avgOrderValueFromOrders = totalOrdersInRange > 0 ? totalRevenueFromOrders / totalOrdersInRange : 0;
      
      // Update dashboard stats with order counts and calculated values
      setDashboardStats(prev => ({
        ...prev,
        totalOrders: totalOrdersInRange,
        totalRevenue: totalRevenueFromOrders, // Use order-based revenue as fallback
        avgOrderValue: avgOrderValueFromOrders,
        pendingOrders: pending.length,
        completedOrders: ordersData.filter(order => order.status === 'Delivered').length,
        cancelledOrders: ordersData.filter(order => order.status === 'Canceled').length,
        recentOrders: ordersData.slice(0, 5) // Get 5 most recent orders
      }));
      
      if (isRefreshRequest) {
        showSuccessNotification("Orders refreshed successfully!");
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      showErrorNotification(error.response?.data?.message || "Error fetching orders.");
    } finally {
      setIsLoadingOrders(false);
    }
  }, [restaurantCredentials, calculateDateRange]);

  const fetchRatings = useCallback(async () => {
    if (!restaurantCredentials) {
      console.warn("No restaurant credentials available for ratings fetch");
      return;
    }

    setIsLoadingRatings(true);
    try {
      // Fetch both ratings list and stats
      const [ratingsResponse, statsResponse] = await Promise.all([
        axios.get(
          `${API_BASE_URL}/ratings/restaurant/${restaurantCredentials.customId}`,
          {
            headers: { Authorization: `Bearer ${restaurantCredentials.token}` },
          }
        ),
        axios.get(
          `${API_BASE_URL}/ratings/restaurant/${restaurantCredentials.customId}/stats`,
          {
            headers: { Authorization: `Bearer ${restaurantCredentials.token}` },
          }
        ).catch(() => null) // Don't fail if stats unavailable
      ]);

      const ratingsData = ratingsResponse.data.ratings || [];
      setRatingsData(ratingsData);
      
      if (statsResponse && statsResponse.data) {
        const stats = statsResponse.data;
        setRatingAnalytics(stats);
        
        // Use stats data for rating stats if available
        setRatingStats({
          averageRating: stats.averageRating || 0,
          totalRatings: stats.totalRatings || 0,
          ratingsBreakdown: stats.ratingsBreakdown || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
        });
      } else if (ratingsData.length > 0) {
        // Fallback to manual calculation if stats not available
        const avgRating = ratingsData.reduce((sum, rating) => sum + (rating.overallRating || 0), 0) / ratingsData.length;
        const breakdown = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        
        ratingsData.forEach(rating => {
          const overallRating = rating.overallRating || 0;
          const rounded = Math.round(overallRating);
          if (breakdown[rounded] !== undefined) {
            breakdown[rounded]++;
          }
        });
        
        setRatingStats({
          averageRating: avgRating,
          totalRatings: ratingsData.length,
          ratingsBreakdown: breakdown
        });
      }
    } catch (error) {
      console.error("Error fetching ratings:", error);
      showErrorNotification("Failed to fetch ratings data");
    } finally {
      setIsLoadingRatings(false);
    }
  }, [restaurantCredentials]);

  const fetchMenuItems = useCallback(async () => {
    if (!restaurantCredentials) {
      console.warn("No restaurant credentials available for menu fetch");
      return;
    }

    setIsLoadingMenu(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/restaurants/mymeals/${restaurantCredentials.customId}`,
        {
          headers: { Authorization: `Bearer ${restaurantCredentials.token}` },
        }
      );
      
      // Updated to handle new API structure - meals are in response.data.meals
      const menuData = response.data.meals || [];
      setMenuItems(menuData);
      
      // Update dashboard stats with menu count
      setDashboardStats(prev => ({
        ...prev,
        totalMenuItems: menuData.length,
        activeMenuItems: menuData.filter(item => item.isAvailable).length
      }));
    } catch (error) {
      console.error("Error fetching menu items:", error);
      showErrorNotification("Failed to fetch menu items");
    } finally {
      setIsLoadingMenu(false);
    }
  }, [restaurantCredentials]);

  // Fetch withdrawal data for the restaurant
  const fetchWithdrawalData = useCallback(async () => {
    if (!restaurantCredentials) {
      console.warn("No restaurant credentials available for withdrawals");
      return;
    }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/withdrawals/restaurant/${restaurantCredentials.customId}`,
        {
          headers: { 
            Authorization: `Bearer ${restaurantCredentials.token}` 
          }
        }
      );

      // Handle withdrawal data response
      if (response.data && response.data.withdrawals) {
        const withdrawals = response.data.withdrawals;
        
        // Calculate total withdrawn amount
        const totalWithdrawn = withdrawals
          .filter(w => w.status === 'completed')
          .reduce((sum, w) => sum + (w.amount || 0), 0);

        // Update dashboard stats with withdrawal data  
        setDashboardStats(prev => ({
          ...prev,
          totalWithdrawn,
          pendingWithdrawals: withdrawals.filter(w => w.status === 'pending').length
        }));
      }
    } catch (error) {
      console.error("Error fetching withdrawal data:", error);
      showErrorNotification("Failed to fetch withdrawal data");
    }
  }, [restaurantCredentials]);

  // Fetch restaurant profile using the correct endpoint
  const fetchRestaurantProfile = useCallback(async () => {
    if (!restaurantCredentials) {
      console.warn("No restaurant credentials available for profile fetch");
      return;
    }

    try {
      // Use the correct restaurant profile endpoint
      const response = await axios.get(
        `${API_BASE_URL}/restaurants/${restaurantCredentials.customId}/profile`,
        {
          headers: { 
            Authorization: `Bearer ${restaurantCredentials.token}` 
          }
        }
      );

      if (response.data) {
        setRestaurant(response.data);
      }
    } catch (error) {
      console.error("Error fetching restaurant profile:", error);
      showErrorNotification("Failed to fetch restaurant profile");
    }
  }, [restaurantCredentials]);

  // Separate useEffect for restaurant data (only runs once on mount)
  useEffect(() => {
    const fetchRestaurantData = async () => {
      if (!restaurantCredentials) {
        showErrorNotification("Authentication required. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        // Use direct axios call to fetch restaurant profile
        const response = await axios.get(
          `${API_BASE_URL}/restaurants/${restaurantCredentials.customId}/profile`,
          {
            headers: { 
              Authorization: `Bearer ${restaurantCredentials.token}` 
            }
          }
        );
        
        if (response.data) {
          setRestaurant(response.data);
        }
      } catch (error) {
        console.error("Error fetching restaurant data:", error);
        showErrorNotification(
          error.response?.data?.message || "Error fetching restaurant data."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurantData();
  }, [restaurantCredentials]);

  // Separate useEffect for dashboard stats (runs when date range changes)
  useEffect(() => {
    if (!restaurantCredentials) return;
    
    // Use unified dashboard data fetch
    fetchDashboardData();
  }, [dateRange, restaurantCredentials]);

  // Separate useEffect for ratings (independent and only runs once)
  useEffect(() => {
    if (!restaurantCredentials) return;
    
    fetchRatings();
  }, [fetchRatings, restaurantCredentials]);

  // Separate useEffect for menu items (independent and only runs once)
  useEffect(() => {
    if (!restaurantCredentials) return;
    
    fetchMenuItems();
  }, [fetchMenuItems, restaurantCredentials]);

  // Separate useEffect for restaurant profile (independent and only runs once)
  useEffect(() => {
    if (!restaurantCredentials) return;
    
    fetchRestaurantProfile();
  }, [fetchRestaurantProfile, restaurantCredentials]);

  // Separate useEffect for withdrawal data (independent and only runs once)
  useEffect(() => {
    if (!restaurantCredentials) return;
    
    fetchWithdrawalData();
  }, [fetchWithdrawalData, restaurantCredentials]);

  const handleWithdrawal = async () => {
    if (!restaurantCredentials) {
      showErrorNotification("Authentication required. Please log in again.");
      return;
    }

    showInfoNotification("Processing withdrawal request...");
    if (!restaurant.walletBalance || parseFloat(restaurant.walletBalance) <= 0) {
      showErrorNotification("Insufficient balance for withdrawal.");
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/withdrawals`,
        {
          restaurantName: restaurant.name,
          amount: parseFloat(restaurant.walletBalance),
          restaurantId: restaurantCredentials.customId
        },
        {
          headers: { 
            Authorization: `Bearer ${restaurantCredentials.token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      showSuccessNotification(response.data.message || "Withdrawal request submitted successfully");
      
      // Update local restaurant state to reflect new balance
      setRestaurant(prev => ({
        ...prev,
        walletBalance: 0
      }));
    } catch (error) {
      showErrorNotification(
        error.response?.data?.message || "Error processing withdrawal."
      );
    }
  };

  // Handle changing date range
  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  // Manual refresh function for data independence
  const refreshAllData = async () => {
    if (!restaurantCredentials) {
      showErrorNotification("Authentication required to refresh data.");
      return;
    }

    setIsRefreshing(true);
    try {
      // Use unified dashboard fetch first
      await fetchDashboardData();
      
      // Then refresh other independent data
      const refreshPromises = [
        fetchTransactionHistory().catch(err => console.error("Transactions refresh failed:", err)),
        fetchMenuItems().catch(err => console.error("Menu refresh failed:", err)),
        fetchRestaurantProfile().catch(err => console.error("Profile refresh failed:", err)),
        fetchWithdrawalData().catch(err => console.error("Withdrawal data refresh failed:", err))
      ];

      await Promise.allSettled(refreshPromises);
      showSuccessNotification("Dashboard data refreshed!");
    } catch (error) {
      console.error("Error during refresh:", error);
      showErrorNotification("Some data may not have refreshed properly.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const toggleActiveStatus = async () => {
    if (!restaurantCredentials || !restaurant) {
      showErrorNotification("Authentication or restaurant data not available.");
      return;
    }

    setIsTogglingStatus(true);
    try {
      await axios.patch(
        `${API_BASE_URL}/restaurants/${restaurant.customId}/toggle-active`,
        { isActive: !restaurant.isActive },
        {
          headers: {
            Authorization: `Bearer ${restaurantCredentials.token}`,
          },
        }
      );
      setRestaurant((prev) => ({ ...prev, isActive: !prev.isActive }));
      showSuccessNotification(`Restaurant is now ${!restaurant.isActive ? "active" : "inactive"}.`);
    } catch (error) {
      showErrorNotification(
        error.response?.data?.message || "Error toggling status."
      );
    } finally {
      setIsTogglingStatus(false);
    }
  };

  // Chart configurations for revenue visualization
  const revenueChartData = {
    labels: dashboardStats.dailyData.labels,
    datasets: [
      {
        label: 'Daily Revenue',
        data: dashboardStats.dailyData.revenue,
        borderColor: 'rgb(251, 191, 36)',
        backgroundColor: 'rgba(251, 191, 36, 0.1)',
        fill: true,
        tension: 0.4,
      }
    ]
  };

  const orderChartData = {
    labels: dashboardStats.dailyData.labels,
    datasets: [
      {
        label: 'Daily Orders',
        data: dashboardStats.dailyData.orders,
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1,
      }
    ]
  };

  const ratingBreakdownData = {
    labels: ['5 Stars', '4 Stars', '3 Stars', '2 Stars', '1 Star'],
    datasets: [
      {
        data: [
          ratingStats.ratingsBreakdown[5] || 0,
          ratingStats.ratingsBreakdown[4] || 0,
          ratingStats.ratingsBreakdown[3] || 0,
          ratingStats.ratingsBreakdown[2] || 0,
          ratingStats.ratingsBreakdown[1] || 0,
        ],
        backgroundColor: [
          '#10B981',
          '#84CC16',
          '#F59E0B',
          '#F97316',
          '#EF4444',
        ],
        borderWidth: 2,
        borderColor: '#fff',
      }
    ]
  };

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 pt-20 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section with Restaurant Info */}
        {restaurant && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-6 rounded-xl shadow-lg mb-8"
          >
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6">
              <div className="flex items-center space-x-4 mb-4 md:mb-0">
                <div className="relative">
                  <img
                    src={restaurant.imageUrl}
                    alt={restaurant.name}
                    className="w-20 h-20 object-cover rounded-xl border-3 border-cheese shadow-lg"
                  />
                  <div className={`absolute -bottom-2 -right-2 w-6 h-6 rounded-full border-2 border-white ${restaurant.isActive ? 'bg-green-500' : 'bg-red-500'}`}></div>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{restaurant.name}</h1>
                  <p className="text-gray-600 mt-1">{restaurant.description}</p>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
                    <span>📍 {restaurant.location}</span>
                    <span>📞 {restaurant.contactNumber}</span>
                    <span>✉️ {restaurant.email}</span>
                  </div>
                  {/* Rating Display */}
                  <div className="flex items-center mt-2">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <StarIconSolid
                          key={i}
                          className={`h-4 w-4 ${
                            i < Math.floor(ratingStats.averageRating)
                              ? 'text-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="ml-2 text-sm text-gray-600">
                      {ratingStats.averageRating.toFixed(1)} ({ratingStats.totalRatings} reviews)
                    </span>
                    {isLoadingRatings && (
                      <div className="ml-2 animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-400"></div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col space-y-3">
                <div className="text-center">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${restaurant.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    <div className={`w-2 h-2 rounded-full mr-2 ${restaurant.isActive ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    {restaurant.isActive ? "Open" : "Closed"}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={toggleActiveStatus}
                    disabled={isTogglingStatus}
                    className={`px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                      restaurant.isActive 
                        ? "bg-red-500 hover:bg-red-600 text-white" 
                        : "bg-yellow-400 hover:bg-yellow-500 text-gray-900"
                    } ${isTogglingStatus ? "opacity-75 cursor-not-allowed" : ""}`}
                  >
                    {isTogglingStatus ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <span>{restaurant.isActive ? "Close Restaurant" : "Open Restaurant"}</span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Wallet Section */}
            <div className="bg-gradient-to-r from-yellow-400 via-yellow-400 to-orange-400 p-6 rounded-xl text-gray-900">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold">Available Balance</h3>
                  <p className="text-3xl font-bold mt-2">
                    ₦{restaurant?.walletBalance?.toLocaleString() || "0.00"}
                  </p>
                  {isLoadingTransactions && (
                    <div className="flex items-center mt-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2"></div>
                      <span className="text-sm">Updating balance...</span>
                    </div>
                  )}
                </div>
                <div>
                  <BanknotesIcon className="h-12 w-12 opacity-80" />
                </div>
              </div>
              <button
                onClick={handleWithdrawal}
                className="bg-gray-900 text-yellow-400 px-6 py-2 rounded-lg font-medium mt-4 hover:bg-gray-800 transition-colors"
                disabled={!restaurant?.walletBalance || restaurant.walletBalance <= 0}
              >
                Request Withdrawal
              </button>
            </div>
          </motion.div>
        )}

        {/* Navigation Tabs */}
        <div className="bg-white p-4 rounded-xl shadow-lg mb-8">
          <div className="flex flex-wrap justify-between items-center gap-2">
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'overview', label: 'Overview', icon: <ChartBarIcon className="w-4 h-4" /> },
                { key: 'transactions', label: 'Transactions', icon: <BanknotesIcon className="w-4 h-4" /> },
                { key: 'orders', label: 'Orders', icon: <ShoppingBagIcon className="w-4 h-4" /> },
                { key: 'ratings', label: 'Ratings & Reviews', icon: <StarIconSolid className="w-4 h-4" /> }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                    activeTab === tab.key
                      ? 'bg-yellow-400 text-gray-900'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
              
              {/* Delivery Fee Management Button */}
              <button
                onClick={() => setShowDeliveryFeeManager(true)}
                className="px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 bg-primary-100 text-primary-600 hover:bg-primary-200"
              >
                <TruckIcon className="w-4 h-4" />
                Delivery Fees
              </button>
            </div>
            
            {/* Refresh Button */}
            <button
              onClick={refreshAllData}
              disabled={isRefreshing}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                isRefreshing 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-primary-500 text-white hover:bg-primary-600'
              }`}
            >
              {isRefreshing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                  <span>Refreshing...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Refresh</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <>
            {/* Date Range Selector */}
            <div className="bg-white p-4 rounded-xl shadow-lg mb-8">
              <div className="flex flex-wrap gap-4">
                {dateRangeOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleDateRangeChange(option.value)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      dateRange === option.value
                        ? "bg-yellow-400 text-gray-900"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Dashboard Stats Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {/* Total Revenue Card */}
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-700">Total Revenue</h3>
                    {isLoadingRevenue ? (
                      <div className="flex items-center justify-center h-12">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-400"></div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-3xl font-bold text-gray-900 mt-2">
                          ₦{dashboardStats.totalRevenue.toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">{dateRangeOptions.find(o => o.value === dateRange)?.label}</p>
                        {/* Debug info */}
                        <p className="text-xs text-gray-400 mt-1">
                          Debug: Revenue ₦{dashboardStats.totalRevenue.toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="text-green-500">
                    <ArrowTrendingUpIcon className="h-8 w-8" />
                  </div>
                </div>
              </div>

              {/* Total Orders Card */}
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-700">Total Orders</h3>
                    {isLoadingOrders ? (
                      <div className="flex items-center justify-center h-12">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-400"></div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-3xl font-bold text-gray-900 mt-2">
                          {dashboardStats.totalOrders}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">{dateRangeOptions.find(o => o.value === dateRange)?.label}</p>
                        {/* Debug info */}
                        <p className="text-xs text-gray-400 mt-1">
                          Debug: {dashboardStats.totalOrders} orders found
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="text-primary-500">
                    <ShoppingBagIcon className="h-8 w-8" />
                  </div>
                </div>
              </div>

              {/* Average Order Value Card */}
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-700">Average Order Value</h3>
                    {isLoadingOrders ? (
                      <div className="flex items-center justify-center h-12">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-400"></div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-3xl font-bold text-gray-900 mt-2">
                          ₦{dashboardStats.avgOrderValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">{dateRangeOptions.find(o => o.value === dateRange)?.label}</p>
                        {/* Debug info */}
                        <p className="text-xs text-gray-400 mt-1">
                          Debug: ₦{dashboardStats.totalRevenue.toLocaleString()} ÷ {dashboardStats.totalOrders} orders
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="text-purple-500">
                    <ChartBarIcon className="h-8 w-8" />
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Revenue Chart */}
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Revenue Trend</h3>
                {dashboardStats.dailyData.labels.length > 0 ? (
                  <Line 
                    data={revenueChartData} 
                    options={{
                      responsive: true,
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: function(value) {
                              return '₦' + value.toLocaleString();
                            }
                          }
                        }
                      },
                      plugins: {
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              return 'Revenue: ₦' + context.parsed.y.toLocaleString();
                            }
                          }
                        }
                      }
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No revenue data available for selected period
                  </div>
                )}
              </div>

              {/* Orders Chart */}
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Orders Trend</h3>
                {dashboardStats.dailyData.labels.length > 0 ? (
                  <Bar 
                    data={orderChartData}
                    options={{
                      responsive: true,
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            stepSize: 1
                          }
                        }
                      }
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No order data available for selected period
                  </div>
                )}
              </div>
            </div>

            {/* Rating Breakdown Chart */}
            {ratingStats.totalRatings > 0 && (
              <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Rating Distribution</h3>
                <div className="max-w-md mx-auto">
                  <Doughnut 
                    data={ratingBreakdownData}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: {
                          position: 'bottom'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'transactions' && (
          <div className="space-y-8">
            {/* Transaction Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-lg font-semibold text-gray-700">Total Earnings</h3>
                <p className="text-3xl font-bold text-green-600 mt-2">
                  ₦{transactionStats.totalEarnings?.toLocaleString() || '0'}
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-lg font-semibold text-gray-700">Total Withdrawals</h3>
                <p className="text-3xl font-bold text-red-600 mt-2">
                  ₦{transactionStats.totalWithdrawals?.toLocaleString() || '0'}
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-lg font-semibold text-gray-700">Current Balance</h3>
                <p className="text-3xl font-bold text-primary-600 mt-2">
                  ₦{transactionStats.currentBalance?.toLocaleString() || '0'}
                </p>
              </div>
            </div>

            {/* Transaction History */}
            <div className="bg-white rounded-xl shadow-lg">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-2xl font-bold text-gray-800">Transaction History</h2>
                <p className="text-gray-600 mt-1">Recent transactions and earnings</p>
              </div>
              
              <div className="p-6">
                {isLoadingTransactions ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mr-3"></div>
                    <span className="text-gray-600">Loading transactions...</span>
                  </div>
                ) : transactionHistory.length > 0 ? (
                  <div className="space-y-4">
                    {transactionHistory.slice(0, 10).map((transaction) => (
                      <div key={transaction._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${
                            transaction.direction === 'credit' ? 'bg-green-500' : 'bg-red-500'
                          }`}></div>
                          <div>
                            <p className="font-medium text-gray-900">{transaction.description}</p>
                            <p className="text-sm text-gray-500">
                              {new Date(transaction.createdAt).toLocaleDateString()} • {transaction.transactionId}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${
                            transaction.direction === 'credit' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {transaction.direction === 'credit' ? '+' : '-'}₦{transaction.amount.toLocaleString()}
                          </p>
                          <p className="text-sm text-gray-500">
                            Balance: ₦{transaction.balanceAfter.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BanknotesIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No Transactions Yet</h3>
                    <p className="text-gray-500 max-w-md mx-auto">
                      Your transaction history will appear here once you start receiving orders.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <RestaurantOrders />
        )}

        {activeTab === 'ratings' && (
          <div className="space-y-8">
            {/* Rating Analytics Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden"
            >
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
                  <StarIconSolid className="w-6 h-6 text-yellow-500" />
                  Rating Analytics
                </h2>
                <p className="text-gray-600 mt-1">
                  Comprehensive insights into your restaurant's customer satisfaction
                </p>
              </div>
              
              <div className="p-6">
                {isLoadingRatings ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mr-3"></div>
                    <span className="text-gray-600">Loading rating analytics...</span>
                  </div>
                ) : ratingStats.totalRatings > 0 ? (
                  <RatingAnalytics 
                    restaurantId={restaurantCredentials?.customId}
                    restaurantName={restaurant?.name}
                  />
                ) : (
                  <div className="text-center py-12">
                    <StarIconSolid className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No Ratings Yet</h3>
                    <p className="text-gray-500 max-w-md mx-auto">
                      Once customers start rating your restaurant, you'll see detailed analytics and insights here.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Recent Reviews Section */}
            {ratingStats.totalRatings > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
              >
                <div className="p-6 border-b border-gray-100">
                  <h3 className="text-xl font-bold text-gray-800">Recent Customer Reviews</h3>
                  <p className="text-gray-600 mt-1">
                    Latest feedback from your customers
                  </p>
                </div>
                
                <div className="p-6">
                  <RatingsList 
                    restaurantId={restaurantCredentials?.customId}
                    showFilters={true}
                    showPagination={true}
                    itemsPerPage={5}
                    showUserActions={false}
                  />
                </div>
              </motion.div>
            )}
          </div>
        )}
        
        {/* Delivery Fee Manager Modal */}
        {showDeliveryFeeManager && (
          <DeliveryFeeManager 
            onClose={() => setShowDeliveryFeeManager(false)}
          />
        )}
      </div>
    </div>
  );
};

export default RestaurantDashboard;